import React, { ComponentProps, ComponentType, useCallback, useRef } from "react";
import { VStack, HStack, View } from "../../native-base";
import { Pressable } from "../../Pressable";
import { Icon } from "../../Icon";
import { Divider } from "../../Divider";
import { useResponsiveTextProps, withResponsiveFlags } from "~/hoc/withResponsiveProps";
import { Text, H2 } from "../../Typography";
import { Checkbox, CheckboxGroup, CheckboxProps } from "../../Checkbox";
import { Radio, RadioGroup } from "../../Radio";
import { IHeadingProps, IRadioGroupProps,  ITextProps, IPressableProps, IFormControlProps, ICheckboxGroupProps } from "native-base";
import ResponsiveTitle from "../../ResponsiveTitle";
import { FormControl } from "../../FormControl";
import { renderHTML } from "~/native-ui/utils";

type SelectionObject = {
    value: string,
    title: string,
    description?: string,
    icon?: string,
    iconFamily?: string,
    isChecked: boolean,
    
    isSmallScreen?: boolean,
    RenderComponent?: ComponentType<CheckboxProps>,
}

type OptionProps = SelectionObject & {
    onChange: (value: string) => void,
    isReadonly?: boolean,
}

const SingleGroup = RadioGroup as React.ComponentType<IRadioGroupProps & IFormControlProps & { label: string }>;
const MultipleGroup = CheckboxGroup as React.ComponentType<ICheckboxGroupProps & IFormControlProps & { label: string, name: string }>;
const _H2 = H2 as React.ComponentType<IHeadingProps>;
const _Text = Text as React.ComponentType<ITextProps>;
const _Pressable = Pressable as React.ComponentType<IPressableProps & { variant: string }>;


const Option = withResponsiveFlags((props: OptionProps) => {
    const preventClick = useRef(false);
    let textProps = useResponsiveTextProps(props.isSmallScreen, 400, 70);

    textProps = props.isSmallScreen ? textProps : {};

    const onPress = React.useCallback(() => {
        const shouldPrevent = preventClick.current;
        preventClick.current = false;
        if (shouldPrevent) return;
        props.onChange(props.value);
        
    }, [props.onChange, props.value]);

    const handledInputChange = React.useCallback((_: boolean) => {
        preventClick.current = true;
        props.onChange(props.value);
    }, [props.onChange, props.value]);

    const iconContainer = props.isSmallScreen ? {} : {px: "3"};
    const stackProps = props.isSmallScreen ? {
        order: 3,
        mt: "3",
        width: "100%",
    } : {
        flex: 1,
    };

    const Input = props.RenderComponent ?? Radio;

    return (
        <_Pressable p="4" onPress={props.isReadonly ? undefined : onPress} disabled={props.isReadonly} variant='muted' background={props.isChecked ? "primary.500:alpha.20" : "transparent"}>
            <HStack space="4" alignItems='center' flexWrap='wrap'>
                {
                    props.icon && 
                    <View {...iconContainer} alignSelf="flex-start">
                        <Icon size="md" type={props.icon} iconFamily={props.iconFamily} />
                    </View>
                }
                <VStack space="1" {...stackProps}>
                    <_H2 {...textProps}>{props.title}</_H2>
                    {props.description && renderHTML(`<span>${props.description}</span>`)}
                </VStack>
                <View ml="auto">
                    <Input value={props.value} readonly={props.isReadonly} checked={props.isChecked} onChange={props.isReadonly ? undefined : handledInputChange}  />
                </View>
            </HStack>
        </_Pressable>
    )
}, { includeKeys: ["isSmallScreen"] });


type BigRadioPropsBase = {
    selections: SelectionObject[],
    trans: (key: string, params?: Record<string, unknown>) => string,
    onChange: (value: any) => void,
    title: string,
    error?: string,
    isinvalid?: boolean,
    tooltip: string,
    multiple?: boolean,
    validationMessage: string,
    description?: string,
    isReadonly?: boolean
};

type Single = BigRadioPropsBase & {
    value: string[],
    multiple: true,
    onChange: (value: string[]) => void,
}

type Multiple = BigRadioPropsBase & {
    value: string[],
    onChange: (value: string) => void,
    multiple: true,
}

type BigRadioProps = Multiple | Single;

export const BigRadio = React.memo(function BigRadio (props: BigRadioProps) {
    const value = ([] as string[]).concat(props.value || []);
    const { Group, Input } = props.multiple ? {
        Group: MultipleGroup,
        Input: Checkbox,
    } as const : {
        Group: RenderRadio,
        Input: Radio,
    } as const

    const handleChange = useCallback<OptionProps['onChange']>((value) => {
        if (!props.multiple) {
            props.onChange(value ?? null);
            return;
        }
        
        let _value = [].concat(props.value || []);
        _value = _value.includes(value) ? _value.filter((x) => x !== value) : _value.concat(value)
        props.onChange(_value);

    }, [props.onChange, props.value, props.multiple,]);

    const error = props.validationMessage || props.error;
    

    return (
        <VStack width="100%" space="2">
            <ResponsiveTitle title={props.title} />
            {props.description && <Text color="muted.500">{props.description}</Text>}
            {
                /** @ts-ignore **/
                error && <FormControl.Label {...errorStyle} label={error} />
            }
            <Group name="" label='' value={value}>
                <VStack divider={<Divider bg={!error ? undefined : "danger.200" } />} w="100%" bg={!error ? "white" : "danger.50"} borderWidth="1px" borderColor={!error ? "muted.300" : "danger.300"} rounded="8">
                    {
                        props.selections.map((option) => (
                            <Option
                                { ...option }
                                key={option.value}
                                title={props.trans(option.title)}
                                description={props.trans(option.description)}
                                onChange={props.isReadonly ? undefined :handleChange}
                                isChecked={value.includes(option.value)}
                                RenderComponent={Input}
                                isReadonly={props.isReadonly}
                            />
                        ))
                    }
                </VStack>
            </Group>
        </VStack>
    );
});

const RenderRadio = (props: Omit<ComponentProps<typeof SingleGroup>, 'value'> & { value: string[] }) => {
    return <SingleGroup {...props} name="" value={props.value[0]} defaultValue={props.defaultValue as unknown as string} />
}

const errorStyle = { color: 'danger.900' };